import { ChangeEvent, FocusEventHandler, useState } from 'react'

import { MenuItem, TextField } from '@mui/material'
import {
  Controller,
  FieldValues,
  RegisterOptions,
  useFormContext,
} from 'react-hook-form'

type IOption = {
  label: string
  value: string | number
}

interface IInput {
  name: string
  label?: string
  id?: string
  type?: 'text' | 'email' | 'number' | 'password' | 'search' | 'tel' | 'select'
  disabled?: boolean
  variant?: 'outlined' | 'standard' | 'filled'
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined
  multiLine?: boolean
  minRows?: string | number
  maxRows?: string | number
  rules?:
    | Omit<
        RegisterOptions<FieldValues, string>,
        'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'
      >
    | undefined
  options?: IOption[] | any
  optionLabel?: string
  optionValue?: string
  readOnly?: boolean
  helperText?: string
}

const Input = ({
  name,
  disabled = false,
  label,
  id,
  type = 'text',
  variant = 'outlined',
  onChange,
  multiLine = false,
  maxRows = 5,
  minRows = 5,
  rules,
  options,
  optionLabel,
  optionValue,
  onBlur,
  readOnly,
  helperText,
}: IInput) => {
  const [isShrink, setIsShrink] = useState(false)
  const { control } = useFormContext()

  const getHelperText = (errorType: string | undefined, errorMsg?: string) => {
    switch (errorType) {
      case 'notOnlySpaces':
        return `Input should not be only blank spaces`
      case 'required':
        return 'This field is required'
      case 'min':
        return `Should be minimum ${rules && rules[errorType]}`
      case 'max':
        return `Should be maximum ${rules && rules[errorType]}`
      case 'minLength':
        return `Minimum  ${rules && rules[errorType]} characters required`
      case 'maxLength':
        return `Maximum  ${rules && rules[errorType]} characters allowed`
      default:
        return errorMsg || 'Enter valid input'
    }
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        ...rules,
        pattern: rules?.pattern || /\S/,
      }}
      disabled={disabled}
      render={({ field, fieldState: { error } }) => {
        if (!isShrink) {
          setIsShrink(
            (Array.isArray(field.value) && field.value.length > 0) ||
              (typeof field.value === 'string' && field.value !== '') ||
              typeof field.value === 'number'
          )
        }
        return (
          <TextField
            fullWidth
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              field.onChange(event)
              if (onChange) {
                onChange(event)
              }
            }}
            onBlur={(event) => {
              field.onBlur()
              setIsShrink(
                (Array.isArray(field.value) && field.value.length > 0) ||
                  (typeof field.value === 'string' && field.value !== '') ||
                  typeof field.value === 'number'
              )
              if (onBlur) {
                onBlur(event)
              }
            }}
            onFocus={() => setIsShrink(true)}
            name={field.name}
            value={field.value}
            ref={field.ref}
            id={id || name}
            label={label}
            type={type}
            select={type === 'select'}
            variant={variant}
            error={!!error?.type}
            helperText={
              (error?.message && getHelperText(error?.type, error?.message)) ||
              (error?.type && getHelperText(error?.type)) ||
              helperText
            }
            multiline={multiLine || false}
            maxRows={maxRows}
            minRows={minRows}
            disabled={disabled}
            InputLabelProps={{
              shrink: isShrink,
            }}
            inputProps={{
              readOnly,
            }}
          >
            {type === 'select' &&
              options &&
              options.map((option: any) => (
                <MenuItem
                  key={option?.value}
                  value={optionValue ? option[optionValue] : option.value}
                >
                  {optionLabel ? option[optionLabel] : option?.label}
                </MenuItem>
              ))}
          </TextField>
        )
      }}
    />
  )
}

export default Input
